// Hand-stubbed (expert × archetype × assertiveness) preview lookup
// for the Configure step's Sample-tagged live preview.
//
// 2 experts × 3 archetypes × 3 assertiveness stops = 18 cells (EN only
// in v1; PT/DE deferred). Responsiveness + Evidence are applied as
// small overlays at the end of `previewLineFor`.

import type { AxisValues, Evidence, Responsiveness } from "../api";

type Cell = string;

const BASE: Record<string, Cell> = {
  // software-spec-author · tech_lead
  "software-spec-author:tech_lead:suggests":
    "If it helps, I could capture this as `change-fargate-bulk-transcoding` — happy to scope to the bits we agree on",
  "software-spec-author:tech_lead:recommends":
    "I'll capture this as `change-fargate-bulk-transcoding` — proposal, design, tasks, and spec deltas",
  "software-spec-author:tech_lead:directs":
    "Change: `change-fargate-bulk-transcoding`. Delta lands in `specs/transcoding/spec.md`",
  // software-spec-author · architect
  "software-spec-author:architect:suggests":
    "Worth weighing the multi-year shape here — invariants we don't want to walk back later",
  "software-spec-author:architect:recommends":
    "I'd lock in the queue-as-source-of-truth invariant before we ship — it constrains the next two changes too",
  "software-spec-author:architect:directs":
    "Queue is the source of truth. Document the invariant in `design.md`",
  // software-spec-author · spec_editor
  "software-spec-author:spec_editor:suggests":
    "Want me to draft a proposal you can edit? I'll only ask if I hit something ambiguous",
  "software-spec-author:spec_editor:recommends":
    "I'll draft the proposal from what's been said — I'll flag two clarifying questions inline",
  "software-spec-author:spec_editor:directs":
    "Drafting now. Two clarifications needed: dedup window, and burst-tier behavior",

  // aws-solutions-architect · whiteboarder
  "aws-solutions-architect:whiteboarder:suggests":
    "You could sketch this as Fargate Spot + regional SQS — rough monthly comes in under four figures",
  "aws-solutions-architect:whiteboarder:recommends":
    "I'd put bulk on Fargate Spot, keep burst on on-demand, SQS in us-east-1 as source of truth",
  "aws-solutions-architect:whiteboarder:directs":
    "Bulk → Fargate Spot. Burst → on-demand. SQS regional. ECR with cross-region replication",
  // aws-solutions-architect · war_reviewer
  "aws-solutions-architect:war_reviewer:suggests":
    "Worth running this through the six pillars — operational excellence and reliability stand out",
  "aws-solutions-architect:war_reviewer:recommends":
    "I'd flag two pillar risks: reliability (no DR runbook) and operational excellence (no Spot-rate metric)",
  "aws-solutions-architect:war_reviewer:directs":
    "Two HIGH-severity findings: missing DR runbook, no Spot interruption-rate metric. Add to risk register",
  // aws-solutions-architect · cost_sentinel
  "aws-solutions-architect:cost_sentinel:suggests":
    "There may be room on cost — Spot mix and idle dev environments are usually where the leak is",
  "aws-solutions-architect:cost_sentinel:recommends":
    "Right-size the dev/staging clusters and add an AWS Budgets alarm at $2,500/mo before this ships",
  "aws-solutions-architect:cost_sentinel:directs":
    "Cost ceiling: $2,500/mo. Right-size dev. Reserved capacity for the burst tier",
};

export interface PreviewInput {
  expertId: string;
  archetypeId: string;
  axes: AxisValues;
  archetypeDisplayName: string;
}

/** Compose the Sample sentence: lookup the (expert × archetype × assertiveness)
 *  base, then apply small responsiveness + evidence overlays. Returns a
 *  user-readable string ready to render. */
export function previewLineFor(input: PreviewInput): string {
  const key = `${input.expertId}:${input.archetypeId}:${input.axes.assertiveness}`;
  let base = BASE[key];
  if (!base) {
    // Fallback for any uncovered cell — keep the preview honest about
    // being a sample. Surfaces during dev when adding a new expert
    // before the cells are authored.
    base =
      input.axes.assertiveness === "suggests"
        ? `As ${input.archetypeDisplayName.toLowerCase()}, I might note something worth checking here`
        : input.axes.assertiveness === "recommends"
          ? `As ${input.archetypeDisplayName.toLowerCase()}, I'd recommend acting on this point`
          : `${input.archetypeDisplayName}: act on this`;
  }

  base = applyResponsivenessOverlay(base, input.axes.responsiveness);
  const tail = evidenceTail(input.axes.evidence);
  return base + tail + ".";
}

function applyResponsivenessOverlay(base: string, r: Responsiveness): string {
  if (r === "balanced") {
    return "Picking up on that — " + lowerFirst(base);
  }
  if (r === "warm") {
    return "Good thread, and to add to it — " + lowerFirst(base);
  }
  return base;
}

function evidenceTail(e: Evidence): string {
  if (e === "cited") return " (docs)";
  if (e === "rigorous") return " (docs · §3, plus cross-ref to the LIA template)";
  return "";
}

function lowerFirst(s: string): string {
  return s.length ? s[0].toLowerCase() + s.slice(1) : s;
}
